import { CurrencyEuroIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo } from "react";
import type { Control, FieldErrors, UseFormSetValue, UseFormTrigger, UseFormWatch } from "react-hook-form";

import ButtonNewItem from "~/components/buttons/ButtonNewItem/ButtonNewItem";
import ComboBox from "~/components/formElements/ComboBox";
import FormInputError from "~/components/formElements/FormInputError";
import RestrictedNumberInput from "~/components/formElements/RestrictedNumberInput";
import Switch from "~/components/formElements/Switch/Switch";
import Textarea from "~/components/formElements/Textarea";
import Hint from "~/components/Hint";
import GlobeIcon from "~/components/icons/GlobeIcon/GlobeIcon";
import SpeechBubbleIcon from "~/components/icons/SpeechBubbleIcon/SpeechBubbleIcon";
import StopwatchIcon from "~/components/icons/StopwatchIcon/StopwatchIcon";
import { useAuth } from "~/contexts/AuthContext";
import useLocationOptions from "~/hooks/form/formOptionsData/useLocationOptions.ts";
import type { Location, UserAvailableLocationType } from "~/modules/location/api/location/locationTypes.ts";
import type { StaffingTimeTrackingSummary } from "~/modules/project/api/staffing/staffingTypes.ts";
import type { UserAvailableDeliverableType } from "~/modules/timeTracking/api/deliverable/deliverableTypes.ts";
import type { TimeTrackingFormData } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { TimeTrackingTypeId } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeTypes.ts";
import Deliverables from "~/modules/timeTracking/components/components/Deliverables";
import Locations from "~/modules/timeTracking/components/components/Locations";
import TimeTrackingFormHeader from "~/modules/timeTracking/components/forms/CreateTimeTrackingForm/components/TimeTrackingFormHeader.tsx";
import {
	DurationSliderPlain,
} from "~/modules/timeTracking/components/forms/formSections/TimeTrackingFormSection/components/DurationSlider.tsx";
import MinuteSelect
	from "~/modules/timeTracking/components/forms/formSections/TimeTrackingFormSection/components/MinuteSelect";
import {
	QuotaBookingSection,
} from "~/modules/timeTracking/components/forms/formSections/TimeTrackingFormSection/components/QuotaBookingSection.tsx";
import { TimeTrackingFormNamesEnum } from "~/modules/timeTracking/types/timeTrackingTypes.ts";
import type { QuotaBookingOption } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import { minutesToWorkdays } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type TimeTrackingFormSectionProps = {
	availableDeliverables: UserAvailableDeliverableType[];
	availableLocations: UserAvailableLocationType[];
	control: Control<TimeTrackingFormData>;
	errors: FieldErrors<TimeTrackingFormData>;
	isEditForm: boolean;
	isSubmitted: boolean;
	locations: Location[];
	quotaBookingOptions: QuotaBookingOption[];
	quotaDrawCents: number;
	selectedStaffingData: StaffingTimeTrackingSummary | null;
	selectedTimeTrackingTypeId: string | null;
	setCurrentFormName: (formId: TimeTrackingFormNamesEnum) => void;
	setValue: UseFormSetValue<TimeTrackingFormData>;
	selectedUserId: string;
	trigger: UseFormTrigger<TimeTrackingFormData>;
	users: User[];
	watch: UseFormWatch<TimeTrackingFormData>;
};

const TimeTrackingFormSection: React.FunctionComponent<TimeTrackingFormSectionProps> = ({
	availableDeliverables,
	availableLocations,
	control,
	errors,
	isEditForm,
	isSubmitted,
	locations,
	quotaBookingOptions,
	quotaDrawCents,
	selectedStaffingData,
	selectedTimeTrackingTypeId,
	setCurrentFormName,
	setValue,
	selectedUserId,
	trigger,
	users,
	watch,
}) => {
	const locationOptions = useLocationOptions();
	const timeTrackingTextValue = watch("text");
	const { user: authenticatedUser } = useAuth();
	const selectedStaffingProjectId = selectedStaffingData?.projectId;

	// this is necessary to correctly display the error message when both minutes and hours are 0
	// @toDo Find a better solution
	const minutes = watch("minutes");
	const hours = watch("hours");

	const isBillableSelected = watch("isBillable");
	const bookOnQuotaSelected = watch("bookOnQuota");

	// A quota booking must be billable (TimeTrackingQuotaBookingRule), so the two switches stay in sync.
	useEffect(() => {
		if (bookOnQuotaSelected && !isBillableSelected) {
			setValue("isBillable", true, { shouldDirty: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bookOnQuotaSelected]);

	useEffect(() => {
		if (!isBillableSelected && bookOnQuotaSelected) {
			setValue("bookOnQuota", false, { shouldDirty: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isBillableSelected]);

	useEffect(() => {
		if (isEditForm || isSubmitted) {
			trigger("hours");
		}
	}, [minutes, hours, trigger, isSubmitted, isEditForm]);

	const remainingManDays = useMemo(() => {
		if (selectedStaffingData) {
			const manDaysSelected = minutesToWorkdays(minutes + hours * 60, 1);
			return (
				selectedStaffingData.manDaysPlanned -
				minutesToWorkdays(selectedStaffingData?.minutesTracked, 1) -
				manDaysSelected
			);
		}
		return 0;
	}, [selectedStaffingData, minutes, hours]);


	const locationsData = useMemo(() => {
		const selectedUsersWorkPlaceLocationId = users.find((u) => u.id === selectedUserId)?.locationId;

		// Get user's current location name first (if any)
		const currentLocation = locations.find((l) => l.id === selectedUsersWorkPlaceLocationId);

		return [
			currentLocation,
			...availableLocations.filter(location => location.id !== selectedUsersWorkPlaceLocationId),
		].filter(Boolean);
	}, [users, availableLocations, locations, selectedUserId]);

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - deliverables
	const deliverablesData = useMemo(() => {
		const preFilteredAvailableDeliverables = availableDeliverables.filter((deliverable) => {
			return (
				(!selectedStaffingProjectId || selectedStaffingProjectId === deliverable.projectId) &&
				selectedTimeTrackingTypeId === deliverable.timeTrackingTypeId &&
				deliverable.userId === selectedUserId  // Only include current user's deliverables
			);
		});

		// Return single array with favorites first, then non-favorites
		return [
			...preFilteredAvailableDeliverables
				.filter(d => d.isFavorite)
				.sort(byObjectProperty("displayName")),
			...preFilteredAvailableDeliverables
				.filter(d => !d.isFavorite)
				.sort(byObjectProperty("displayName")),
		];
	}, [availableDeliverables, selectedStaffingProjectId, selectedTimeTrackingTypeId, selectedUserId]);

	const handeLocationClick = useCallback(
		(value: string, event: MouseEvent) => {
			event.preventDefault();
			setValue("locationName", value, { shouldDirty: true });
			trigger("locationName");
		},
		[setValue, trigger],
	);

	const handleDeliverableClick = useCallback(
		(value: string, event: MouseEvent) => {
			event.preventDefault();
			const index = timeTrackingTextValue?.indexOf(value) ?? -1;
			const isTrimmedValueEqual = timeTrackingTextValue?.trim() === value;

			if (index >= 0 || isTrimmedValueEqual) {
				let newValue = timeTrackingTextValue?.replace(new RegExp(`\\s*${value}\\s*`, "g"), "") ?? "";
				newValue = newValue.replace(",,", ",").replace(/^,|,$| $/g, "");

				setValue("text", newValue.trim(), { shouldDirty: true });
			} else {
				const newValue = timeTrackingTextValue?.trim().length === 0 ? value : `${timeTrackingTextValue}, ${value}`;
				setValue("text", newValue, { shouldDirty: true });
			}

			trigger("text");
		},
		[timeTrackingTextValue, trigger, setValue],
	);

	const text = watch("text");

	const durationSliderMarks = useMemo(() => {
		const startHour = 0;
		const endHour = 8;

		return Array.from({ length: endHour - startHour + 1 },
			(_, i) => startHour + i).map((hour) => {
			return {
				value: hour * 60,
				label: hour.toString(),
			};
		});

	}, []);

	const handleDurationSliderChange = useCallback((totalMinutes: number) => {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		setValue("hours", hours, { shouldDirty: true });
		setValue("minutes", minutes, { shouldDirty: true });
		trigger("hours");
	}, [setValue, trigger]);

	const isProjectTimeTracking = !!selectedStaffingData;
	const isBillable = isProjectTimeTracking && selectedStaffingData?.isBillable;

	return (
		<>
			<div className="flex items-center justify-between">
				<TimeTrackingFormHeader icon={<StopwatchIcon className="w-7 fill-primary-700" />}>Zeiterfassung</TimeTrackingFormHeader>
			</div>
			<div className="mb-0 flex flex-row items-center gap-2 gap-x-10">
				<div className="flex flex-row items-center justify-center gap-2">
					<div className="w-[3.6rem]">
						<RestrictedNumberInput
							autoFocus={true}
							minValue={0}
							maxValue={23}
							name={"hours"}
							control={control}
							placeholder="08"
							className="text-center text-xl text-primary-900"
						/>
					</div>
					<span className="font-bold text-primary-500">h</span>
					<MinuteSelect name="minutes"
								  control={control} />
					<span className="font-bold text-primary-500">m</span>
				</div>
				<DurationSliderPlain
					value={minutes + hours * 60}
					hourMarks={durationSliderMarks}
					onChange={handleDurationSliderChange}
				/>
			</div>
			<div className="block">
				{(isEditForm || isSubmitted) && errors.hours && <FormInputError>{errors.hours.message}</FormInputError>}
			</div>

			{selectedTimeTrackingTypeId === TimeTrackingTypeId.Project ?
				<div className="mb-2 mt-4 flex items-center justify-start gap-x-1 text-xs text-gray-500">
					<span>Verbleibende Tage: </span><span className={clsx(remainingManDays >= 0 ? "text-gray-500" : "text-danger-500")}>{formatNumberWithComma(remainingManDays || 0, 2)} PT</span>
				</div> : <div className="h-6" />}

			{selectedTimeTrackingTypeId !== TimeTrackingTypeId.PartTime &&
				<>
					<div className="h-2" />
					<TimeTrackingFormHeader icon={<GlobeIcon className="w-7  fill-primary-700" />}>Ort</TimeTrackingFormHeader>
					<div className="h-3" />
					<ComboBox
						control={control}
						name="locationName"
						optionsData={locationOptions}
						error={errors.locationName?.message}
					/>
					<div className="mt-2 flex flex-row flex-wrap items-center justify-start gap-2">
						{selectedUserId === authenticatedUser?.id && <div>
							<ButtonNewItem onClick={() => setCurrentFormName(TimeTrackingFormNamesEnum.LOCATION_FORM)}
										   size="timeTrackingTag" />
						</div>}
						<Locations data={locationsData as unknown as Location[]}
								   onClick={handeLocationClick} />
					</div>
					<div className="h-10" />
					<TimeTrackingFormHeader icon={
						<SpeechBubbleIcon className="w-7 fill-primary-700 pt-[0.3rem]" />}>Buchungstext</TimeTrackingFormHeader>
					<div className="h-3" />
					<Textarea
						control={control}
						name="text"
					/>
					<div className="mt-2 flex flex-row flex-wrap items-center justify-start gap-x-3 gap-y-2">
						{selectedUserId === authenticatedUser?.id && <div>
							<ButtonNewItem onClick={() => setCurrentFormName(TimeTrackingFormNamesEnum.DELIVERABLE_FORM)}
										   size="timeTrackingTag"
							/>
						</div>}
						{deliverablesData.length > 0 && (
							<>
								<Deliverables
									currentDeliverablesString={text}
									data={deliverablesData}
									onClick={handleDeliverableClick} />
							</>
						)}
					</div>
					{isBillable && quotaBookingOptions.length > 0 &&
						<QuotaBookingSection control={control}
											 quotaBookingOptions={quotaBookingOptions}
											 quotaDrawCents={quotaDrawCents} />}
					{isProjectTimeTracking && <>
						<div className="mt-10 flex items-center justify-between">
							<TimeTrackingFormHeader icon={
								<CurrencyEuroIcon className="-mr-1 size-8 fill-primary-700" />}
							highlightText={!isBillableSelected}
							>
								{!isBillableSelected ? "Nicht abrechenbar" : "Abrechenbar"}
							</TimeTrackingFormHeader>
							{isBillable && <Switch control={control}
												   inputSize="xs"
												   name="isBillable" />}
						</div>
						<div className="mt-3 max-w-full overflow-hidden">
							{!isBillable && <Hint theme="error"
												  size="sm">
								<p className="whitespace-pre-wrap break-words">Dieses Staffing wurde im Projekt
									als &quot;nicht
									abrechenbar&quot; gekennzeichnet. Daher wird diese Erfassung bei der
									Rechnungsstellung
									ignoriert.</p>
							</Hint>}
							{isBillable && <div className="space-y-4 text-sm">
								<p>
									Sollen Aufwände in einem Projekt aus Kulanz nicht abgerechnet werden, muss der
									Schalter ausgeschaltet werden.
								</p>
								<p className="font-light italic">
									Beispiel: Aufwände, die über das Budget hinaus erbracht werden.
								</p>
								<p>
									<strong>Diesen Schalter nicht nutzen</strong> für bereits abgerechnete Aufwände -
									bspw.
									Aufwände wurden bereits im Vorjahr oder einer erletzten Rechnung abgerechnet.
								</p>
							</div>}
						</div>
					</>
					}
				</>}

		</>
	);
};

export default TimeTrackingFormSection;
